import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Truck, Upload, MapPin, Clock, Phone, Package, CheckCircle2, X, Loader2,
  Route, Printer, FileText, Trash2, ChevronRight, Play, Pen, ArrowLeft, Camera, Image as ImageIcon, Plus
} from "lucide-react";
import Navigation from "@/components/Navigation";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ROUTE_PRESETS = [
  { id: "skane", label: "Egen bil - Skane (lev. tisdagar)", name: "Skane" },
  { id: "smaland", label: "Egen bil - Smaland/Blekinge (lev. torsdagar)", name: "Smaland/Blekinge" },
  { id: "halland", label: "Egen bil - Halland (lev. fredagar)", name: "Halland" },
  { id: "special", label: "Specialrunda", name: "Specialrunda" },
];
const DEFAULT_START_ADDRESS = "Råstorpsvägen 12, 28593 Markaryd";

export default function DeliveryPage() {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [routeName, setRouteName] = useState("");
  const [startAddress, setStartAddress] = useState(DEFAULT_START_ADDRESS);
  const [startTime, setStartTime] = useState("08:00");
  const [signStop, setSignStop] = useState(null);
  const [stagedFiles, setStagedFiles] = useState([]); // { file, preview, id }

  // Compress an image file: resize to max 2000px on the longest edge + JPEG quality 0.85
  const compressImage = (file, { maxDim = 2000, quality = 0.85 } = {}) =>
    new Promise((resolve) => {
      try {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          const { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          // Skip if already small enough
          if (scale === 1 && file.size < 1.5 * 1024 * 1024) {
            URL.revokeObjectURL(url);
            resolve(file);
            return;
          }
          const w = Math.round(width * scale);
          const h = Math.round(height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(url);
              if (!blob || blob.size >= file.size) {
                // Compression didn't help — keep original
                resolve(file);
                return;
              }
              const newName = file.name.replace(/\.(heic|heif|png|webp)$/i, ".jpg");
              const compressed = new File([blob], newName, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressed);
            },
            "image/jpeg",
            quality
          );
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(file);
        };
        img.src = url;
      } catch {
        resolve(file);
      }
    });

  const addStagedFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    let savedBytes = 0;
    const processed = await Promise.all(
      files.map(async (file) => {
        const isImage = file.type.startsWith("image/");
        const finalFile = isImage ? await compressImage(file) : file;
        savedBytes += Math.max(0, file.size - finalFile.size);
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        return {
          id,
          file: finalFile,
          preview: isImage ? URL.createObjectURL(finalFile) : null,
          isImage,
        };
      })
    );
    setStagedFiles((prev) => [...prev, ...processed]);
    const savedKb = Math.round(savedBytes / 1024);
    if (savedKb > 50) {
      toast.success(`${processed.length} fil${processed.length > 1 ? "er" : ""} tillagd${processed.length > 1 ? "a" : ""} (komprimerade, sparade ${savedKb} KB)`);
    } else {
      toast.success(`${processed.length} fil${processed.length > 1 ? "er" : ""} tillagd${processed.length > 1 ? "a" : ""}`);
    }
  };

  const removeStagedFile = (id) => {
    setStagedFiles((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (entry?.preview) URL.revokeObjectURL(entry.preview);
      return prev.filter((e) => e.id !== id);
    });
  };

  const clearStagedFiles = () => {
    stagedFiles.forEach((e) => { if (e.preview) URL.revokeObjectURL(e.preview); });
    setStagedFiles([]);
  };

  const fetchRoutes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deliveries`);
      setRoutes(res.data.routes || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const fetchRoute = async (id) => {
    try {
      const res = await axios.get(`${API}/deliveries/${id}`);
      setSelectedRoute(res.data);
    } catch { toast.error("Kunde inte hamta rutt"); }
  };

  // Poll processing routes
  useEffect(() => {
    if (!selectedRoute || selectedRoute.status !== "processing") return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/deliveries/${selectedRoute.id}`);
        setSelectedRoute(res.data);
        if (res.data.status !== "processing") clearInterval(interval);
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [selectedRoute?.id, selectedRoute?.status]);

  const handleFileInput = async (e) => {
    // Capture file list reference BEFORE clearing the input value
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file later
    await addStagedFiles(files);
  };

  const createRoute = async () => {
    if (stagedFiles.length === 0) {
      toast.error("Lagg till minst en fil eller ett foto forst");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    stagedFiles.forEach((entry) => {
      formData.append("files", entry.file);
    });
    formData.append("route_name", routeName || `Leveransrutt ${new Date().toLocaleDateString("sv-SE")}`);
    formData.append("start_address", startAddress);
    formData.append("start_time", startTime);
    try {
      const res = await axios.post(`${API}/deliveries/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success(`${stagedFiles.length} filer uppladdade — bearbetar...`);
      clearStagedFiles();
      setRouteName("");
      await fetchRoute(res.data.id);
      await fetchRoutes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uppladdning misslyckades");
    } finally { setUploading(false); }
  };

  const deleteRoute = async (id) => {
    if (!confirm("Ta bort rutten?")) return;
    try {
      await axios.delete(`${API}/deliveries/${id}`);
      toast.success("Rutt borttagen");
      if (selectedRoute?.id === id) setSelectedRoute(null);
      fetchRoutes();
    } catch { toast.error("Kunde inte ta bort"); }
  };

  const markDelivered = async (routeId, stopId, signature) => {
    try {
      await axios.post(`${API}/deliveries/${routeId}/stops/${stopId}/status`, {
        status: "delivered",
        signature: signature || null,
      });
      toast.success("Levererad!");
      await fetchRoute(routeId);
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const markSkipped = async (routeId, stopId) => {
    try {
      await axios.post(`${API}/deliveries/${routeId}/stops/${stopId}/status`, { status: "skipped" });
      toast.info("Hoppade over");
      await fetchRoute(routeId);
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  if (selectedRoute) {
    return (
      <RouteDetailView
        route={selectedRoute}
        onBack={() => setSelectedRoute(null)}
        onMarkDelivered={markDelivered}
        onMarkSkipped={markSkipped}
        onSign={(stop) => setSignStop(stop)}
        signStop={signStop}
        onSignClose={() => setSignStop(null)}
        onSignComplete={async (stopId, sig) => {
          await markDelivered(selectedRoute.id, stopId, sig);
          setSignStop(null);
        }}
        onRefresh={() => fetchRoute(selectedRoute.id)}
      />
    );
  }

  return (
    <div data-testid="delivery-page">
      <Navigation />
      <div className="container mx-auto px-4 py-6 space-y-6">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Truck className="h-6 w-6" />
        Leveransrutter
      </h1>

      {/* Create route */}
      <Card>
        <CardHeader><CardTitle className="text-base">Skapa ny rutt</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select
              value={selectedPreset}
              onValueChange={(val) => {
                setSelectedPreset(val);
                const preset = ROUTE_PRESETS.find(p => p.id === val);
                if (preset) setRouteName(preset.name);
              }}
            >
              <SelectTrigger data-testid="route-preset-select">
                <SelectValue placeholder="Valj rutt..." />
              </SelectTrigger>
              <SelectContent>
                {ROUTE_PRESETS.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={startAddress}
              onChange={(e) => setStartAddress(e.target.value)}
              placeholder="Startadress"
              data-testid="start-address-input"
            />
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              data-testid="start-time-input"
            />
            <Input
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              placeholder="Eget namn (valfritt)"
              className="text-sm"
              data-testid="route-name-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild disabled={uploading} variant="outline" className="gap-2" data-testid="add-files-btn">
              <label className="cursor-pointer">
                <Upload className="h-4 w-4" />
                Lagg till filer
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={handleFileInput}
                  disabled={uploading}
                />
              </label>
            </Button>
            <Button asChild disabled={uploading} variant="outline" className="gap-2" data-testid="add-photo-btn">
              <label className="cursor-pointer">
                <Camera className="h-4 w-4" />
                Ta foto
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileInput}
                  disabled={uploading}
                />
              </label>
            </Button>
            <span className="text-xs text-muted-foreground">Lagg till flera filer/foton, tryck sedan pa "Skapa rutt"</span>
          </div>

          {/* Staged files preview */}
          {stagedFiles.length > 0 && (
            <div className="border rounded-lg p-3 bg-muted/30 space-y-2" data-testid="staged-files-list">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  {stagedFiles.length} fil{stagedFiles.length > 1 ? "er" : ""} vald{stagedFiles.length > 1 ? "a" : ""}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-red-500"
                  onClick={clearStagedFiles}
                  disabled={uploading}
                  data-testid="clear-staged-btn"
                >
                  Rensa alla
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {stagedFiles.map((entry) => (
                  <div
                    key={entry.id}
                    className="relative group bg-background border rounded-md overflow-hidden flex items-center gap-2 pr-2"
                    data-testid={`staged-file-${entry.id}`}
                  >
                    {entry.isImage ? (
                      <img
                        src={entry.preview}
                        alt={entry.file.name}
                        className="h-12 w-12 object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-12 w-12 flex items-center justify-center bg-red-500/10 shrink-0">
                        <FileText className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                    <div className="min-w-0 max-w-[160px]">
                      <p className="text-xs font-medium truncate">{entry.file.name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {(entry.file.size / 1024).toFixed(0)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-red-500"
                      onClick={() => removeStagedFile(entry.id)}
                      disabled={uploading}
                      data-testid={`remove-staged-${entry.id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                onClick={createRoute}
                disabled={uploading || stagedFiles.length === 0}
                className="gap-2 w-full sm:w-auto"
                data-testid="create-route-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Route className="h-4 w-4" />}
                {uploading ? "Skapar rutt..." : `Skapa rutt (${stagedFiles.length} fil${stagedFiles.length > 1 ? "er" : ""})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Routes list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
      ) : routes.length === 0 ? (
        <Card className="p-8 text-center">
          <Route className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground">Inga leveransrutter skapade annu</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {routes.map((route) => (
            <Card
              key={route.id}
              className="cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => fetchRoute(route.id)}
              data-testid={`route-${route.id}`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${route.status === "ready" ? "bg-green-500/10" : route.status === "processing" ? "bg-yellow-500/10" : route.status === "completed" ? "bg-blue-500/10" : "bg-muted/30"}`}>
                    {route.status === "processing" ? <Loader2 className="h-5 w-5 text-yellow-500 animate-spin" /> :
                     route.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-blue-500" /> :
                     <Route className="h-5 w-5 text-green-500" />}
                  </div>
                  <div>
                    <p className="font-medium">{route.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {route.total_stops || 0} stopp
                      {route.total_estimated_minutes ? ` — ca ${Math.round(route.total_estimated_minutes)} min` : ""}
                      {route.status === "processing" && ` — Bearbetar ${route.parsed_files || 0}/${route.total_files || 0} filer...`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={route.status === "ready" ? "success" : route.status === "completed" ? "secondary" : "outline"}>
                    {route.status === "ready" ? "Redo" : route.status === "processing" ? "Bearbetar" : route.status === "completed" ? "Klar" : route.status}
                  </Badge>
                  <Button
                    size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); deleteRoute(route.id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}


function RouteDetailView({ route, onBack, onMarkDelivered, onMarkSkipped, onSign, signStop, onSignClose, onSignComplete, onRefresh }) {
  const isProcessing = route.status === "processing";
  const stops = route.stops || [];
  const delivered = stops.filter(s => s.status === "delivered").length;
  const total = stops.length;

  return (
    <div>
      <div className="print:hidden"><Navigation /></div>
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 print:px-0 print:py-0 print:space-y-2 print:max-w-none" data-testid="route-detail">
      {/* Header */}
      <div className="flex items-center justify-between print:block">
        <div className="flex items-center gap-3 print:gap-2">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn" className="print:hidden">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold print:text-2xl">{route.name}</h1>
            <p className="text-sm text-muted-foreground print:text-black">
              Start: {route.start_time} | {total} stopp
              {route.total_estimated_minutes ? ` | Ca ${Math.floor(route.total_estimated_minutes / 60)}h ${route.total_estimated_minutes % 60}min` : ""}
            </p>
            <p className="hidden print:block text-xs text-black mt-1">
              Startadress: {route.start_address || "—"}
            </p>
          </div>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => window.print()} data-testid="print-btn">
            <Printer className="h-4 w-4" /> Skriv ut
          </Button>
        </div>
      </div>

      {isProcessing && (
        <Card className="border-yellow-500/30 bg-yellow-500/5 print:hidden">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-yellow-500 animate-spin" />
            <div>
              <p className="font-medium">Bearbetar orderbekraftelser...</p>
              <p className="text-sm text-muted-foreground">Lasar {route.parsed_files || 0} av {route.total_files || 0} filer</p>
            </div>
            <Progress value={((route.parsed_files || 0) / (route.total_files || 1)) * 100} className="flex-1 h-2" />
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {!isProcessing && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 print:hidden">
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Stopp</p><p className="text-2xl font-bold">{total}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Levererade</p><p className="text-2xl font-bold text-green-500">{delivered}/{total}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Kortid</p><p className="text-2xl font-bold">{route.total_drive_minutes || 0} min</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total tid</p><p className="text-2xl font-bold">{route.total_estimated_minutes ? `${Math.floor(route.total_estimated_minutes / 60)}h ${route.total_estimated_minutes % 60}m` : "—"}</p></CardContent></Card>
        </div>
      )}

      {/* Stops list */}
      <div className="space-y-2 print:space-y-1">
        {stops.map((stop, idx) => (
          <Card
            key={stop.id}
            className={`transition-colors print:break-inside-avoid print:shadow-none print:border-black ${stop.status === "delivered" ? "border-green-500/30 bg-green-500/5 opacity-75" : stop.status === "skipped" ? "border-muted opacity-50" : ""}`}
            data-testid={`stop-${idx}`}
          >
            <CardContent className="p-3 sm:p-4 print:p-2">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold print:border print:border-black print:bg-white print:text-black ${stop.status === "delivered" ? "bg-green-500 text-white" : stop.status === "skipped" ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}`}>
                  {stop.status === "delivered" ? <CheckCircle2 className="h-4 w-4 print:hidden" /> : null}
                  <span className={stop.status === "delivered" ? "hidden print:inline" : ""}>{stop.order || idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium print:text-base">{stop.customer_name || "Okand kund"}</p>
                      <p className="text-sm text-muted-foreground print:text-black flex items-center gap-1">
                        <MapPin className="h-3 w-3 shrink-0 print:hidden" />
                        {stop.address}, {stop.postal_code} {stop.city}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      {stop.eta && <p className="text-sm font-mono font-bold print:text-base">{stop.eta}</p>}
                      {stop.drive_time_text && <p className="text-xs text-muted-foreground print:text-black">{stop.drive_time_text} dit</p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 print:gap-3">
                    {stop.order_number && <Badge variant="outline" className="text-xs print:border-black print:text-black">#{stop.order_number}</Badge>}
                    {stop.phone && (
                      <a href={`tel:${stop.phone}`} className="text-xs text-blue-500 print:text-black flex items-center gap-0.5">
                        <Phone className="h-3 w-3 print:hidden" />{stop.phone}
                      </a>
                    )}
                    <span className="text-xs text-muted-foreground print:text-black flex items-center gap-0.5">
                      <Clock className="h-3 w-3 print:hidden" />{stop.estimated_minutes} min vid stopp
                    </span>
                  </div>
                  {stop.products && stop.products.length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground print:text-black">
                      {stop.products.map((p, pi) => (
                        <span key={pi} className="inline-flex items-center gap-1 mr-3">
                          <Package className="h-3 w-3 print:hidden" /> {p.quantity}x {p.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {stop.notes && <p className="text-xs text-amber-500 print:text-black mt-1">{stop.notes}</p>}
                  {/* Print-only signature line */}
                  <div className="hidden print:block mt-3 text-xs text-black">
                    Signatur: ______________________________
                  </div>
                </div>
              </div>
              {stop.status === "pending" && (
                <div className="flex gap-2 mt-3 ml-11 print:hidden">
                  <Button size="sm" className="gap-1" onClick={() => onSign(stop)} data-testid={`sign-${idx}`}>
                    <Pen className="h-3 w-3" /> Signera & leverera
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onMarkDelivered(route.id, stop.id)} data-testid={`deliver-${idx}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Levererad
                  </Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onMarkSkipped(route.id, stop.id)}>
                    Hoppa over
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Signature Dialog */}
      {signStop && (
        <SignatureDialog
          stop={signStop}
          onClose={onSignClose}
          onComplete={(sig) => onSignComplete(signStop.id, sig)}
        />
      )}
    </div>
    </div>
  );
}


function SignatureDialog({ stop, onClose, onComplete }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const saveSignature = () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onComplete(dataUrl);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Signatur — {stop.customer_name}</DialogTitle>
          <DialogDescription>{stop.address}, {stop.postal_code} {stop.city}</DialogDescription>
        </DialogHeader>
        <div className="border rounded-lg bg-white overflow-hidden touch-none">
          <canvas
            ref={canvasRef}
            width={400}
            height={200}
            className="w-full cursor-crosshair"
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
            data-testid="signature-canvas"
          />
        </div>
        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" size="sm" onClick={clearCanvas}>Rensa</Button>
          <Button size="sm" onClick={saveSignature} disabled={!hasDrawn} data-testid="save-signature">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Bekrafta leverans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
